import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-premium';
import { PaginatedDataGrid, StackedTextCell } from '@retail/components';
import { useRowColorGrouping } from '@retail/hooks';
import {
  IconButton,
  MgSupplierItemSearchField,
  TotalItemsLoader,
} from '@retail/products/components';
import { formatPrice } from '@retail/products/utils';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { PriceListItem, PurchaseAgreement } from '@retail/purchase-agreements/types';
import { CheckIcon2, ExcelIcon2, WarningIcon } from '@shared/custom-icons';
import { Pagination } from '@shared/hooks';
import { dataGridWithCellBordersClass } from '@shared/styles';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  selectedAgreements: PurchaseAgreement[];
  priceListItemPrices: PriceListItem[];
  priceListItemLength: number;
  pagination: Pagination;
  isFetching: boolean;
  isLoadingExport: boolean;
  includeSearchField?: boolean;
  exportToExcel: () => Promise<void>;
  setSearchQuery: (query: string) => void;
}

const baseColumnProps: Partial<GridColDef<PriceListItem>> = {
  width: 180,
  sortable: false,
  flex: 1,
  headerAlign: 'left',
  align: 'left',
};

export function PriceListTable({
  selectedAgreements,
  priceListItemPrices,
  priceListItemLength,
  pagination,
  isFetching,
  isLoadingExport,
  includeSearchField = false,
  exportToExcel,
  setSearchQuery,
}: Props) {
  const t = usePurchaseAgreementsTFunction();
  const { i18n } = useTranslation();
  const { getRowColorByIdGroups } = useRowColorGrouping();

  const mgItemNumbers = useMemo(
    () => priceListItemPrices.map((item) => item.mgItemNumber),
    [priceListItemPrices]
  );

  const handleGetRowClassName = useCallback(
    (value: GridRowClassNameParams<PriceListItem>) => {
      return getRowColorByIdGroups(
        mgItemNumbers,
        value.row.mgItemNumber,
        value.indexRelativeToCurrentPage
      );
    },
    [getRowColorByIdGroups, mgItemNumbers]
  );

  const columns: GridColDef<PriceListItem>[] = useMemo(() => {
    const nameColDef: GridColDef<PriceListItem> = {
      ...baseColumnProps,
      field: 'primaryText',
      headerName: t('purchaseAgreements.agreement.columns.primaryText'),
      minWidth: 160,
      rowSpanValueGetter: (_, row) => row.mgItemNumber,
      renderCell: ({ row }) => {
        return <StackedTextCell topLine={row.primaryText} bottomLine={row.secondaryText} />;
      },
    };
    const idColDef: GridColDef<PriceListItem> = {
      ...baseColumnProps,
      field: 'finfo/mgItem',
      headerName: t('purchaseAgreements.agreement.columns.finfoAndMgItemId'),
      minWidth: 86,
      rowSpanValueGetter: (_, row) => row.mgItemNumber,
      renderCell: ({ row }) => {
        return <StackedTextCell topLine={row.mgItemNumber} bottomLine={row.finfoNumber} />;
      },
    };

    const priceTypeColDef: GridColDef<PriceListItem> = {
      ...baseColumnProps,
      maxWidth: 100,
      field: 'priceType',
      headerName: t('purchaseAgreements.agreement.actions.priceType'),
      renderCell: ({ row }) => {
        return row.agreementPrices[0]?.priceType ?? '-';
      },
    };

    const agreementPriceColDefs: GridColDef<PriceListItem>[] = selectedAgreements.map(
      (selectedAgreement) => ({
        field: selectedAgreement.id + '',
        minWidth: 100,
        ...baseColumnProps,
        renderHeader: () => (
          <StackedTextCell topLine={selectedAgreement.name} bottomLine={selectedAgreement.id} />
        ),
        renderCell: ({ row }) => {
          const agreementPrice = row.agreementPrices.find(
            (agreement) => agreement.agreementId === selectedAgreement.id
          );

          const validFrom =
            agreementPrice?.validFrom && dayjs(agreementPrice.validFrom).format('L');

          const validTo = agreementPrice?.validTo && dayjs(agreementPrice.validTo);
          //Agreements that has no end date is set to year 9999
          const toDate = validTo && validTo.year() !== 9999 ? validTo.format('L') : undefined;

          const price = agreementPrice?.price && formatPrice(agreementPrice.price, i18n.language);

          const validated = !!agreementPrice?.validationStatus;
          return (
            <Stack flexDirection="row" flexGrow={1} gap={1} alignItems="center">
              <Stack>
                <StackedTextCell
                  topLine={t('purchaseAgreements.priceList.pricePerUnit', {
                    price: price ?? '-',
                    unit: agreementPrice?.priceUnit ?? '-',
                  })}
                  bottomLine={validFrom}
                  nullableBottomLine
                />
                {toDate && (
                  <Stack flexDirection="row" gap={1}>
                    <Tooltip title={t('purchaseAgreements.priceList.expires')}>
                      <div>
                        <WarningIcon color="warning" fontSize="small" />
                      </div>
                    </Tooltip>
                    {toDate}
                  </Stack>
                )}
              </Stack>
              {validated && (
                <Tooltip title={t('purchaseAgreements.priceList.priceIsValidated')}>
                  <div>
                    <CheckIcon2 />
                  </div>
                </Tooltip>
              )}
            </Stack>
          );
        },
      })
    );
    return [nameColDef, idColDef, priceTypeColDef, ...agreementPriceColDefs];
  }, [i18n.language, selectedAgreements, t]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack flexDirection="row" alignItems="center" px={3} justifyContent="space-between">
        <TotalItemsLoader
          itemsShowing={priceListItemLength}
          totalItems={pagination.totalCount}
          isFetching={isFetching}
        />
        <Stack flexDirection="row" alignItems="center" gap={1}>
          {includeSearchField && (
            <MgSupplierItemSearchField
              isDisabled={false}
              onSubmitSearch={setSearchQuery}
              includeLabel={false}
              size="small"
            />
          )}
          <IconButton
            onClick={exportToExcel}
            loading={isLoadingExport}
            disabled={!selectedAgreements.length}
          >
            <ExcelIcon2 fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <PaginatedDataGrid
        rows={priceListItemPrices}
        columns={columns}
        loading={isFetching}
        className={dataGridWithCellBordersClass}
        rowHeight={60}
        rowSpanning
        getRowId={(row) => `${row.mgItemNumber}${row.agreementPrices[0]?.priceType}`}
        getRowClassName={handleGetRowClassName}
        rowCount={pagination.totalCount}
        gridPagination={pagination}
        slots={{
          noRowsOverlay: () => (
            <Stack alignItems="center" justifyContent="center" height="100%">
              {selectedAgreements.length === 0 ? (
                <Typography variant="h4" color="text.secondary">
                  {t('purchaseAgreements.priceList.seePrices')}
                </Typography>
              ) : (
                <Typography variant="h4" color="text.secondary">
                  {t('purchaseAgreements.priceList.noMatches')}
                </Typography>
              )}
            </Stack>
          ),
        }}
      />
    </Box>
  );
}
